import { EPISODE_COBALT_BLUE } from '@/constants/colors';
import { abbreviateLabel } from '@/utils/episodeLabel';
import moment from 'moment';

import { createVisitEvent } from '../../VisitsScheduling/calendar/createVisitEvent';
import {
    CalendarBar,
    CalendarDayMarker,
    CalendarTile,
} from '../../VisitsScheduling/calendar/types';
import { PatientEpisodeCalendarVisitsQuery } from './PatientEpisodeCalendar.gql.types';

// Home-usage-visit and recertification windows render as gray labeled span bars.
const HUV_WINDOW_COLOR = '#9CA3AF';
const RECERTIFICATION_COLOR = '#6B7280';

export const createHUVEvent = (startDate: Date, endDate: Date, title: string): CalendarBar => {
    return {
        kind: 'bar',
        id: `${title}-window`,
        start: moment(startDate).startOf('day').toDate(),
        end: moment(endDate).endOf('day').toDate(),
        label: title,
        color: HUV_WINDOW_COLOR,
    };
};

export const createRecertificationEvent = (recertWindow: {
    startDate: Date | string;
    endDate: Date | string;
}): CalendarBar[] => {
    return [
        {
            kind: 'bar',
            id: 'recertification-window',
            start: moment(recertWindow.startDate).startOf('day').toDate(),
            end: moment(recertWindow.endDate).endOf('day').toDate(),
            label: 'Recertification',
            color: RECERTIFICATION_COLOR,
        },
    ];
};

/**
 * Episode span primitives: admission start/end render as chevron day-markers; the recert
 * window renders as a span bar.
 */
export const allEvents = ({
    day0,
    endDate,
    numberInSequence,
    typeLabel,
    recertWindow,
}: {
    day0: Date;
    endDate: Date;
    numberInSequence: number | null;
    typeLabel: string;
    recertWindow: { startDate: Date; endDate: Date } | null;
}): { bars: CalendarBar[]; markers: CalendarDayMarker[] } => {
    const episodeLabel = numberInSequence ? `#${numberInSequence} ` : '';
    const markers: CalendarDayMarker[] = [
        {
            id: 'episode-day0',
            date: day0,
            label: `${typeLabel} ${episodeLabel}Start`,
            shortLabel: `${abbreviateLabel(typeLabel)} ${episodeLabel}Start`,
            color: EPISODE_COBALT_BLUE,
            shape: 'start',
        },
        {
            id: 'episode-end',
            date: endDate,
            label: `${typeLabel} ${episodeLabel}End`,
            shortLabel: `${abbreviateLabel(typeLabel)} ${episodeLabel}End`,
            color: EPISODE_COBALT_BLUE,
            shape: 'end',
        },
    ];

    const bars = recertWindow ? createRecertificationEvent(recertWindow) : [];
    return { bars, markers };
};

type Visit = NonNullable<PatientEpisodeCalendarVisitsQuery['visits']>['items'][number];
type VisitWithEffectiveDate = Visit & { effectiveDate: Date };

function isVisitWithEffectiveDate(visit: Visit): visit is VisitWithEffectiveDate {
    return visit.effectiveDate !== null;
}

/**
 * Map an episode-query visit into calendar tiles (one per clinician appointment); visits with
 * no effective date or no appointments are skipped. Adapts the query's visit + appointment
 * shape into the shared normalized createVisitEvent input.
 */
export const episodeVisitToTiles = (visit: Visit): CalendarTile[] => {
    if (!isVisitWithEffectiveDate(visit)) {
        return [];
    }
    return (visit.clinicianAppointments ?? []).map((appt) =>
        createVisitEvent({
            id: `${visit.id}`,
            date: visit.effectiveDate,
            timezone: visit.timezone,
            state: visit.state,
            outcome: visit.outcome,
            disciplines: visit.disciplines,
            appointment: {
                id: appt.id,
                startDatetime: appt.startDatetime ? new Date(appt.startDatetime) : null,
                endDatetime: appt.endDatetime ? new Date(appt.endDatetime) : null,
                clinician: appt.clinician,
                services: appt.services,
            },
        }),
    );
};
